/**
 * D-317 §1 — the ticket window is stretched by dragging: the line between the
 * conversation and the side panel sets the side panel's width; the width is kept
 * per user in localStorage. Pure (no DOM): unit-tested in
 * src/utils/__tests__/ticketPaneModel.test.ts.
 */
export const SIDE_MIN_PX = 256;
export const SIDE_MAX_PX = 560;
export const SIDE_DEFAULT_PX = 320; // the former fixed 20rem
export const SIDE_STEP_PX = 16;

export const paneStorageKey = (userId?: string | null): string => `ote.ticketPane.side.${userId || 'anon'}`;

export function clampSide(px: number): number {
  if (!Number.isFinite(px)) return SIDE_DEFAULT_PX;
  return Math.round(Math.min(SIDE_MAX_PX, Math.max(SIDE_MIN_PX, px)));
}

type Store = Pick<Storage, 'getItem' | 'setItem'> | null | undefined;

/** The user's width, or the default (no storage / blocked / garbage). */
export function readSideWidth(store: Store, userId?: string | null): number {
  try {
    const v = store?.getItem(paneStorageKey(userId));
    if (v === null || v === undefined || v === '') return SIDE_DEFAULT_PX;
    return clampSide(Number(v));
  } catch {
    return SIDE_DEFAULT_PX;
  }
}

export function writeSideWidth(store: Store, userId: string | null | undefined, px: number): void {
  try {
    store?.setItem(paneStorageKey(userId), String(clampSide(px)));
  } catch {
    /* private window / blocked storage: the width just is not kept */
  }
}

/**
 * The side panel's width from the pointer: it is the grid's second column, at
 * the inline END (the left in Hebrew / RTL, the right in LTR).
 */
export function sideWidthFromPointer(p: { clientX: number; gridLeft: number; gridRight: number; rtl: boolean }): number {
  return clampSide(p.rtl ? p.clientX - p.gridLeft : p.gridRight - p.clientX);
}

/** Arrow keys on the handle move the line the way the arrow points. */
export function sideWidthFromKey(current: number, key: string, rtl: boolean): number | null {
  if (key === 'Home') return SIDE_MIN_PX;
  if (key === 'End') return SIDE_MAX_PX;
  if (key !== 'ArrowLeft' && key !== 'ArrowRight') return null;
  // LTR: the side panel is on the right, so the line moving left widens it; RTL the opposite.
  const towardSide = rtl ? key === 'ArrowLeft' : key === 'ArrowRight';
  return clampSide(current + (towardSide ? -SIDE_STEP_PX : SIDE_STEP_PX));
}
